/**
 * The first video encoder a page opens can take seconds to start: the browser
 * loads its codec library or opens a hardware session. A recording that starts
 * during that time loses its first seconds (frames arriving while the encoder is
 * not ready are dropped). So an encoder is opened and closed once ahead of time,
 * when the recording setup appears, and recording waits for it to finish.
 */
const WARM_UP_CONFIG: VideoEncoderConfig = {
  codec: 'avc1.42001f',
  width: 1280,
  height: 720,
  bitrate: 2_000_000,
  latencyMode: 'realtime',
};

/** Never hold the person up longer than this; a slow start is still better than none. */
const MAX_WAIT_MS = 15_000;

let warming: Promise<void> | null = null;

async function warmUp(): Promise<void> {
  if (typeof VideoEncoder === 'undefined' || typeof OffscreenCanvas === 'undefined') return;
  const support = await VideoEncoder.isConfigSupported(WARM_UP_CONFIG).catch(() => null);
  if (!support?.supported) return;
  const encoder = new VideoEncoder({ output: () => undefined, error: () => undefined });
  try {
    encoder.configure(WARM_UP_CONFIG);
    const canvas = new OffscreenCanvas(WARM_UP_CONFIG.width, WARM_UP_CONFIG.height);
    canvas.getContext('2d')?.fillRect(0, 0, 8, 8);
    for (let index = 0; index < 2; index++) {
      const frame = new VideoFrame(canvas, { timestamp: index * 33_333 });
      encoder.encode(frame, { keyFrame: index === 0 });
      frame.close();
    }
    await encoder.flush();
  } finally {
    if (encoder.state !== 'closed') encoder.close();
  }
}

/** Starts warming up (once per page). */
export function warmUpVideoEncoder(): Promise<void> {
  warming ??= warmUp().catch(() => undefined);
  return warming;
}

/** Resolves when the encoder is ready, or after a while regardless. */
export function encoderReady(): Promise<void> {
  return Promise.race([warmUpVideoEncoder(), new Promise<void>((resolve) => setTimeout(resolve, MAX_WAIT_MS))]);
}
